import { motion } from "framer-motion";
import type { TooltipRenderProps } from "react-joyride";
import ScoutAvatarBadge from "./ScoutAvatarBadge";
import type { ScoutState } from "@/tours/types";

type ScoutTooltipProps = TooltipRenderProps & {
    scoutState?: ScoutState;
    totalSteps?: number;
    actionLabel?: string;
    endsTour?: boolean;
    isInGroup?: boolean;
    onRequestCancel?: () => void;
    disableNext?: boolean;
};

function ProgressBar({ total, current }: { total: number; current: number }) {
    const pct = total > 1 ? Math.round(((current + 1) / total) * 100) : 100;
    return (
        <div className="flex items-center gap-2 min-w-[80px]">
            <div className="flex-1 h-1.5 rounded-full bg-shadow-100 overflow-hidden">
                <div
                    className="h-full rounded-full bg-max-500 transition-all duration-300 ease-out"
                    style={{ width: `${pct}%` }}
                />
            </div>
            <span className="text-[11px] text-shadow-300 font-jakarta tabular-nums">
                {current + 1}/{total}
            </span>
        </div>
    );
}

export default function ScoutTooltip({
    index,
    step,
    primaryProps,
    tooltipProps,
    isLastStep,
    scoutState = "idle",
    totalSteps = 1,
    actionLabel,
    endsTour,
    onRequestCancel,
    disableNext,
    isInGroup,
}: ScoutTooltipProps) {
    const isAction = !!actionLabel;
    // Only show "Got it!" on the true final step — not the last step of a group (which pops back to main flow)
    const isFinalStep = !!endsTour || (isLastStep && !isInGroup);

    return (
        <div {...tooltipProps}>
            <motion.div
                className="flex items-center max-w-[calc(100vw-3rem)]"
                initial={index === 0 ? { opacity: 0, scale: 0.9, y: 8 } : false}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
            >
                <ScoutAvatarBadge state={scoutState} />

                {/* Speech tail — overlaps card edge by 1px to avoid seam */}
                <div
                    className="w-0 h-0 flex-shrink-0 relative z-10 mr-[-1px] ml-1.5"
                    style={{
                        borderTop: "8px solid transparent",
                        borderBottom: "8px solid transparent",
                        borderRight: "10px solid white",
                    }}
                />

                <div
                    className="bg-white rounded-[14px] max-w-[300px] min-w-0 flex-1"
                    style={{
                        padding: "14px 16px",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)",
                    }}
                >
                    <>
                        {step.title && (
                            <h3 className="text-base font-bold text-shadow-700 mb-1.5 font-jakarta">
                                {step.title as string}
                            </h3>
                        )}

                        <p className="text-shadow-400 mb-3 text-[14px] font-jakarta leading-relaxed break-words">
                            {step.content as React.ReactNode}
                        </p>

                        <footer>
                            {isAction ? (
                                <>
                                    <div className="flex items-center gap-1.5 text-max-900 mb-2.5">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M9 11.24V7.5a2.5 2.5 0 015 0v3.74c1.21-.81 2-2.18 2-3.74C16 4.46 13.54 2 10.5 2S5 4.46 5 7.5c0 1.56.79 2.93 2 3.74zm9.84 4.63l-4.54-2.26c-.17-.07-.35-.11-.54-.11H13v-6c0-.83-.67-1.5-1.5-1.5S10 6.67 10 7.5v10.74l-3.43-.72c-.08-.01-.15-.03-.24-.03-.31 0-.59.13-.79.33l-.79.8 4.94 4.94c.27.27.65.44 1.06.44h6.79c.75 0 1.33-.55 1.44-1.28l.75-5.27c.01-.07.02-.14.02-.2 0-.62-.38-1.16-.91-1.38z" />
                                        </svg>
                                        <span className="text-[13px] font-semibold font-jakarta break-words">
                                            {actionLabel}
                                        </span>
                                    </div>
                                    <div className="flex items-center flex-wrap gap-2">
                                        <button
                                            onClick={() => onRequestCancel?.()}
                                            className="text-shadow-300 text-[13px] hover:text-shadow-400 transition-colors"
                                        >
                                            Skip tour
                                        </button>
                                        <div className="ml-auto">
                                            <ProgressBar total={totalSteps} current={index} />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center flex-wrap gap-2">
                                    <button
                                        {...primaryProps}
                                        disabled={disableNext}
                                        className={`rounded-lg px-5 py-2 text-sm font-semibold transition-colors ${disableNext ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-max-900 text-white hover:bg-max-800"}`}
                                    >
                                        {isFinalStep ? "Got it!" : "Next"}
                                    </button>

                                    {!isFinalStep && (
                                        <button
                                            onClick={() => onRequestCancel?.()}
                                            className="text-shadow-300 text-[13px] hover:text-shadow-400 transition-colors"
                                        >
                                            Skip
                                        </button>
                                    )}

                                    <div className="ml-auto">
                                        <ProgressBar total={totalSteps} current={index} />
                                    </div>
                                </div>
                            )}
                        </footer>
                    </>
                </div>
            </motion.div>
        </div>
    );
}
